import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { track } from "@/lib/analytics";

const items = [
  {
    n: "01",
    title: "Strategi varje månad",
    body: "Vi börjar med en kort workshop. Mål, kanaler, kalender. Du får en plan — inte ett pitchdeck.",
  },
  {
    n: "02",
    title: "Produktion i takt",
    body: "Vi spelar in i block en gång i månaden och klipper löpande. Du bokar inga möten kring nästa film.",
  },
  {
    n: "03",
    title: "Leverans varje vecka",
    body: "Färdiga klipp i alla format ni behöver — sociala, sajt, YouTube. Redo att publicera.",
  },
  {
    n: "04",
    title: "Optimering & nästa drop",
    body: "Vi tittar på vad som funkat och bygger vidare. Ingen omstart, ingen ny offert.",
  },
];

export function PulseExplainerDialog({ trigger }: { trigger: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) track("hero_pulse_dialog_open", { location: "hero" });
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl gap-0 border-white/10 bg-surface/95 p-0 backdrop-blur-xl">
        <div className="relative p-8 md:p-10">
          <div className="text-xs uppercase tracking-[0.22em] text-mint">Pulse på 60 sekunder</div>
          <DialogTitle className="mt-3 text-balance text-3xl font-semibold leading-[1.1] md:text-4xl">
            Så funkar Pulse.
          </DialogTitle>
          <DialogDescription className="mt-3 max-w-lg text-pretty text-sm text-muted-foreground md:text-base">
            En produktion. Ett team. Fyra steg som upprepas varje månad — så ni alltid har färsk film, utan offerter.
          </DialogDescription>

          <ol className="mt-7 space-y-4">
            {items.map((it) => (
              <li key={it.n} className="grid grid-cols-[auto_1fr] gap-4 rounded-2xl border border-white/5 bg-background/40 p-4">
                <span className="font-mono text-[11px] tracking-[0.18em] text-mint">{it.n}</span>
                <div>
                  <div className="text-sm font-semibold md:text-base">{it.title}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{it.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/pulse"
              onClick={() => track("hero_pulse_dialog_full_page", { location: "hero" })}
              className="text-sm text-foreground/80 underline-offset-4 hover:text-mint hover:underline"
            >
              Läs hela sidan om Pulse →
            </Link>
            <Link
              to="/kontakt"
              onClick={() => track("hero_pulse_dialog_book", { location: "hero" })}
              className="group inline-flex items-center gap-2 rounded-full bg-peach px-5 py-2.5 text-sm font-semibold text-peach-foreground transition-shadow hover:shadow-[0_16px_40px_-12px] hover:shadow-peach/50"
            >
              Boka strategimöte
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
